import { useState, useEffect, useRef, useMemo } from "react";
import clsx from "clsx";

const DropdownRow = ({ opt, onSelect, isSelected }) => (
  <button
    onClick={() => onSelect(opt)}
    className={clsx(
      "w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl active:scale-[0.98] transition-all text-left group",
      isSelected
        ? "bg-primary bg-opacity-10 hover:bg-opacity-20"
        : "hover:bg-pampas",
    )}
    title={opt.label}
  >
    <div
      className={clsx(
        "w-8 h-8 rounded-xl flex items-center justify-center shrink-0",
        opt.iconBg || "bg-cloudy-gray",
      )}
    >
      {opt.iconEl}
    </div>
    <p
      className={clsx(
        "text-sm truncate flex-1 min-w-0",
        isSelected
          ? "font-bold-poppins text-primary"
          : "font-medium-poppins text-dawn-gray",
      )}
    >
      {opt.label}
    </p>
    {isSelected ? (
      <svg
        viewBox="0 0 12 10"
        fill="none"
        className="w-3 h-3 text-primary shrink-0"
      >
        <path
          d="M1 5l3.5 3.5L11 1"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ) : (
      <svg
        viewBox="0 0 6 10"
        fill="none"
        className="w-1.5 h-2.5 text-silver-md-light group-hover:text-cloudy-gray shrink-0 transition-colors"
      >
        <path
          d="M1 1l4 4-4 4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )}
  </button>
);

const SplitDropdown = ({
  label,
  options = [],
  onSelect,
  value,
  configSectionLabel = "Custom Reports",
  multiSelect = false,
  placeholder,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const baseItems = options.filter((o) => o.group !== "config");
  const configItems = options.filter((o) => o.group === "config");

  const isRowSelected = (opt) =>
    multiSelect
      ? Array.isArray(value) && value.includes(opt.value)
      : opt.value === value;

  const handleRowSelect = (opt) => {
    if (!multiSelect) {
      onSelect(opt);
      setOpen(false);
      return;
    }
    const current = Array.isArray(value) ? value : [];
    const newValue = current.includes(opt.value)
      ? current.filter((v) => v !== opt.value)
      : [...current, opt.value];
    onSelect(newValue);
  };

  const triggerLabel = useMemo(() => {
    if (!multiSelect) return label;

    if (Array.isArray(value) && value.length > 0) {
      const selectedLabels = options
        .filter((o) => value.includes(o.value))
        .map((o) => o.label);
      if (selectedLabels.length === 1) return selectedLabels[0];
      if (selectedLabels.length > 1)
        return `${selectedLabels[0]} (+${selectedLabels.length - 1})`;
    }
    return placeholder || label;
  }, [multiSelect, value, options, placeholder, label]);

  return (
    <div ref={ref} className={clsx("relative", className)}>
      {/* Trigger */}
      <div className="flex items-center w-full border border-silver-md-light rounded-full overflow-hidden shadow-sm">
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex-1 min-w-0 px-3 sm:px-4 py-2 text-xs sm:text-sm text-dawn-gray font-medium-poppins hover:bg-pampas transition-colors whitespace-nowrap truncate text-left"
        >
          {triggerLabel}
        </button>
        <button
          onClick={() => setOpen((o) => !o)}
          className="px-2 sm:px-3 py-2 border-l border-silver-md-light hover:bg-pampas transition-colors"
        >
          <svg
            viewBox="0 0 10 6"
            fill="none"
            className={clsx(
              "w-3 h-3 text-dawn-gray transition-transform duration-200",
              open ? "rotate-0" : "-rotate-90",
            )}
          >
            <path
              d="M1 1l4 4 4-4"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Panel */}
      {open && (
        <div className="absolute right-0 top-[calc(100%+8px)] w-64 bg-white border border-silver-md-light rounded-2xl shadow-xl z-50">
          {/* Base actions — always visible */}
          {baseItems.length > 0 && (
            <div className="px-2 pt-2 pb-1.5 flex flex-col gap-0.5">
              {baseItems.map((opt) => (
                <DropdownRow
                  key={opt.value}
                  opt={opt}
                  onSelect={handleRowSelect}
                  isSelected={isRowSelected(opt)}
                />
              ))}
            </div>
          )}

          {/* Config section — scrollable */}
          {configItems.length > 0 && (
            <>
              {baseItems.length > 0 && (
                <div className="h-px bg-silver-md-light mx-3" />
              )}
              <div className="px-4 pt-2.5 pb-1">
                <p className="text-[10px] font-medium-poppins text-cloudy-gray uppercase tracking-widest">
                  {configSectionLabel}
                </p>
              </div>
              <div className="px-2 pb-2 max-h-52 overflow-y-auto flex flex-col gap-0.5 scroll-smooth">
                {configItems.map((opt) => (
                  <DropdownRow
                    key={opt.value}
                    opt={opt}
                    onSelect={handleRowSelect}
                    isSelected={isRowSelected(opt)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SplitDropdown;
